import type { PepLocale } from "../i18n/types";

/** Contact page editorial — FR/EN, portfolio-safe. */

export const CONTACT_HERO_BADGES = [
  {
    id: "local",
    labelFr: "Expertise locale",
    labelEn: "Local expertise",
  },
  {
    id: "guidance",
    labelFr: "Accompagnement personnalisé",
    labelEn: "Personalized guidance",
  },
  {
    id: "outcomes",
    labelFr: "Résultats concrets",
    labelEn: "Concrete outcomes",
  },
  {
    id: "partnership",
    labelFr: "Partenariat durable",
    labelEn: "Lasting partnership",
  },
] as const;

export const CONTACT_REASONS = [
  {
    id: "strategy",
    titleFr: "Conseil stratégique",
    titleEn: "Strategic advice",
    bodyFr: "Clarifiez vos priorités et identifiez le périmètre ITaaS le plus utile pour démarrer.",
    bodyEn: "Clarify priorities and identify the most useful ITaaS scope to start with.",
  },
  {
    id: "tailored",
    titleFr: "Accompagnement sur mesure",
    titleEn: "Tailored support",
    bodyFr: "Nous adaptons la composition des modules à votre organisation et à votre rythme.",
    bodyEn: "We adapt module composition to your organization and your pace.",
  },
  {
    id: "delivery",
    titleFr: "Mise en œuvre rapide",
    titleEn: "Fast implementation",
    bodyFr: "Déploiement progressif, paramétrage et formation pour ancrer l’usage rapidement.",
    bodyEn: "Progressive rollout, configuration and training to embed usage quickly.",
  },
  {
    id: "proximity",
    titleFr: "Support de proximité",
    titleEn: "Nearby support",
    bodyFr: "Une équipe ITaaS RDC à Lubumbashi — échanges en français, réactivité locale.",
    bodyEn: "An ITaaS RDC team in Lubumbashi — French-language exchanges, local responsiveness.",
  },
] as const;

export const CONTACT_FAQ = [
  {
    id: "delay",
    qFr: "Sous quel délai recevons-nous une réponse ?",
    qEn: "How quickly will we hear back?",
    aFr: "Notre équipe commerciale répond sous 48 heures ouvrées pour les demandes de démo, d’information ou de devis assisté.",
    aEn: "Our sales team replies within 48 business hours for demo, information or assisted quote requests.",
  },
  {
    id: "configure",
    qFr: "Quand utiliser le configurateur plutôt que ce formulaire ?",
    qEn: "When should we use the configurator instead of this form?",
    aFr: "Pour un devis package (modules, profils, estimation), préférez le configurateur. Utilisez ce formulaire pour un échange humain, une démo ou un accompagnement.",
    aEn: "For a package quote (modules, profiles, estimate), prefer the configurator. Use this form for a human conversation, a demo or guided support.",
  },
  {
    id: "demo",
    qFr: "Comment se déroule une démonstration ?",
    qEn: "What does a demonstration look like?",
    aFr: "Nous planifions un échange adapté à votre secteur et aux produits concernés, puis nous présentons les parcours utiles pour votre organisation.",
    aEn: "We schedule a conversation tailored to your sector and products, then walk through the journeys that matter for your organization.",
  },
  {
    id: "start",
    qFr: "Peut-on démarrer avec un seul module ?",
    qEn: "Can we start with a single module?",
    aFr: "Oui. ITaaS Suite est modulaire : vous activez un premier périmètre, puis vous composez d’autres modules selon vos priorités.",
    aEn: "Yes. ITaaS Suite is modular: activate a first scope, then compose additional modules as priorities evolve.",
  },
] as const;

export const CONTACT_SECTORS = [
  { value: "", labelFr: "Sélectionner…", labelEn: "Select…" },
  { value: "education", labelFr: "Éducation", labelEn: "Education" },
  { value: "healthcare", labelFr: "Santé", labelEn: "Healthcare" },
  { value: "mining", labelFr: "Mines & industrie", labelEn: "Mining & industry" },
  { value: "commerce", labelFr: "Commerce & distribution", labelEn: "Commerce & distribution" },
  { value: "security", labelFr: "Sécurité privée", labelEn: "Private security" },
  { value: "public", labelFr: "Administration / ONG", labelEn: "Public sector / NGOs" },
  { value: "other", labelFr: "Autre", labelEn: "Other" },
] as const;

export const CONTACT_SUPPORT_IMAGE = "/assets/contact/itaas-call-center-agent.png";

export const CONTACT_DEPLOYMENT_OPTIONS = [
  { value: "saas", labelFr: "SaaS", labelEn: "SaaS" },
  { value: "on_premise", labelFr: "On-Premise", labelEn: "On-Premise" },
  { value: "private_cloud", labelFr: "Cloud privé", labelEn: "Private cloud" },
  { value: "hybrid", labelFr: "Hybride", labelEn: "Hybrid" },
  { value: "advice", labelFr: "Je souhaite être conseillé", labelEn: "I want advice" },
] as const;

export const CONTACT_USERS_OPTIONS = [
  { value: "1-20", labelFr: "1–20", labelEn: "1–20" },
  { value: "20-100", labelFr: "20–100", labelEn: "20–100" },
  { value: "100-500", labelFr: "100–500", labelEn: "100–500" },
  { value: "500+", labelFr: "500+", labelEn: "500+" },
] as const;

export const CONTACT_URGENCY_OPTIONS = [
  { value: "immediate", labelFr: "Immédiat", labelEn: "Immediate" },
  { value: "1_month", labelFr: "Sous 1 mois", labelEn: "Within 1 month" },
  { value: "3_months", labelFr: "Sous 3 mois", labelEn: "Within 3 months" },
  { value: "future", labelFr: "Projet futur", labelEn: "Future project" },
] as const;

export function contactCopy(locale: PepLocale) {
  const en = locale === "en";
  return {
    seoTitle: en ? "Contact | ITaaS Suite" : "Contact | ITaaS Suite",
    seoDescription: en
      ? "Contact ITaaS RDC for a demo, project discussion or guided support. Lubumbashi, DRC — reply within 48 business hours."
      : "Contactez ITaaS RDC pour une démo, un projet ou un accompagnement. Lubumbashi, RDC — réponse sous 48 h ouvrées.",
    seoKeywords: en
      ? ["contact ITaaS RDC", "ITaaS Suite demo", "digital transformation DRC"]
      : ["contact ITaaS RDC", "démo ITaaS Suite", "transformation digitale RDC"],

    heroEyebrow: en ? "Contact" : "Contact",
    heroH1: en ? "Let’s talk about your ITaaS project" : "Parlons de votre projet ITaaS",
    heroLead: en
      ? "Our team supports your digital transformation with an approach tailored to your organization."
      : "Notre équipe vous accompagne dans votre transformation digitale avec une approche adaptée à votre organisation.",
    heroVisualAlt: en
      ? "Conceptual illustration of connected support and digital accompaniment — not a product screenshot."
      : "Illustration conceptuelle du support connecté et de l’accompagnement digital — ce n’est pas une capture produit.",

    formTitle: en ? "Send a message" : "Envoyer un message",
    formLead: en
      ? "Tell us about your organization — we reply within 48 business hours."
      : "Parlez-nous de votre organisation — réponse sous 48 heures ouvrées.",
    labelName: en ? "Full name *" : "Nom complet *",
    labelEmail: en ? "Work email *" : "Email professionnel *",
    labelPhone: en ? "Phone *" : "Téléphone *",
    labelCompany: en ? "Company *" : "Entreprise *",
    labelCountry: en ? "Country *" : "Pays *",
    labelSector: en ? "Sector" : "Secteur",
    labelType: en ? "Request type *" : "Type de demande *",
    labelProducts: en ? "Products concerned" : "Produit concerné",
    labelMessage: en ? "Message" : "Message",
    messagePlaceholder: en
      ? "Describe your project, users, and priorities…"
      : "Décrivez votre projet, nombre d’utilisateurs, priorités…",
    confidentiality: en
      ? "Your information is handled confidentially by the ITaaS RDC team."
      : "Vos informations sont traitées de manière confidentielle par l’équipe ITaaS RDC.",
    submitCta: en ? "Send message" : "Envoyer le message",
    submitPending: en ? "Sending…" : "Envoi…",
    appointCta: en ? "Book a meeting" : "Prendre rendez-vous",

    napTitle: en ? "ITaaS RDC details" : "Coordonnées ITaaS RDC",
    napRole: en
      ? "Publisher and operator of ITaaS Suite"
      : "Éditeur et opérateur d’ITaaS Suite",
    napAddress: en ? "Address" : "Adresse",
    napPhone: en ? "Phone" : "Téléphone",
    napEmail: en ? "Email" : "Email",
    napWeb: en ? "Website" : "Site web",
    napAbout: en ? "About the company →" : "À propos de l’entreprise →",

    supportTitle: en ? "ITaaS RDC Support" : "Support ITaaS RDC",
    supportSubtitle: en
      ? "A team available to support you before, during and after your project."
      : "Une équipe disponible pour vous accompagner avant, pendant et après votre projet.",
    supportBody: en
      ? "Our specialists answer commercial, technical and functional questions quickly."
      : "Nos spécialistes répondent rapidement à vos questions commerciales, techniques et fonctionnelles.",
    supportCta: en ? "Contact us" : "Nous contacter",
    supportImageAlt: en
      ? "ITaaS customer service advisor"
      : "Conseillère service client ITaaS",
    labelDeployment: en ? "Deployment mode" : "Mode de déploiement",
    labelUsers: en ? "Number of users" : "Nombre d’utilisateurs",
    labelUrgency: en ? "Urgency" : "Urgence",
    successReference: en ? "Your reference" : "Votre référence",

    reasonsEyebrow: en ? "Guidance" : "Accompagnement",
    reasonsH2: en ? "Why contact us?" : "Pourquoi nous contacter ?",
    reasonsLead: en
      ? "A structured conversation to move from interest to a clear next step."
      : "Un échange structuré pour passer de l’intérêt à une prochaine étape claire.",

    faqEyebrow: en ? "FAQ" : "FAQ",
    faqH2: en ? "Frequently asked questions" : "Questions fréquentes",
    docsTitle: en ? "Documentation" : "Documentation",
    docsBody: en
      ? "Explore product docs, guides and technical references."
      : "Explorez la documentation produit, les guides et références techniques.",
    docsCta: en ? "View documentation" : "Voir la documentation",

    finalH2: en
      ? "Ready to transform your organization?"
      : "Prêt à transformer votre organisation ?",
    finalLead: en
      ? "Let’s build the solution that fits your needs together."
      : "Construisons ensemble la solution adaptée à vos besoins.",
    finalCta: en ? "Request a demonstration" : "Demander une démonstration",

    configureWarnTitle: en
      ? "Looking for a package quote (modules + profiles + estimate)?"
      : "Besoin d’un devis package (modules + profils + estimation) ?",
    configureWarnBody: en
      ? "Compose your solution in the configurator — faster and more accurate than a generic quote request."
      : "Composez votre solution dans le configurateur — plus rapide et plus précis qu’une demande de devis générique.",
    configureWarnCta: en ? "Open the configurator →" : "Ouvrir le configurateur →",
    configureWarnKeep: en
      ? "You can still send this form if you prefer human assistance first."
      : "Vous pouvez toujours envoyer ce formulaire si vous préférez un échange humain d’abord.",
    configureHint: en
      ? "Need a tailored package (modules + profiles + estimate)?"
      : "Besoin d’un package personnalisé (modules + profils + estimation) ?",
    configureHintLink: en ? "Open the configurator →" : "Ouvrir le configurateur →",

    successThanks: (name: string) =>
      en ? `Thank you, ${name}!` : `Merci ${name} !`,
    successNext: en ? "What happens next?" : "Prochaines étapes",
    successStep1: en
      ? "A sales advisor reviews your needs and selected products."
      : "Un conseiller commercial analyse votre besoin et les produits sélectionnés.",
    successStep2: en
      ? "We schedule a call or demo tailored to your organization."
      : "Nous planifions un échange ou une démo adaptée à votre organisation.",
    successStep3: en
      ? "You receive a formal quote or trial access, depending on your request."
      : "Vous recevez un devis formel ou un accès d’essai, selon votre demande.",
    backCatalog: en ? "Back to catalog" : "Retour au catalogue",
    viewProduct: en ? "View product" : "Voir le produit",
  };
}
